import { useState, useMemo, useEffect } from 'react';
import { Box, InputAdornment, TextField, Typography, useTheme } from '@mui/material';
import { AutoIsoPlayer } from '@/features/autoIso/types/AutoIsoModel';
import { TeamModel } from '../types/TeamModel';
import { TeamPlayersList } from './TeamPlayersList';
import { Search } from 'lucide-react';
import { Tracking } from '@/shared/services/tracking';

/** Quiet period before a search is reported, in ms. */
const SEARCH_TRACK_DEBOUNCE_MS = 600;

type Props = {
  team: TeamModel;
  players: AutoIsoPlayer[];
  onPlayerClick?: (player: AutoIsoPlayer) => void;
  singleCurrentlyTrackedObjectId?: number | null;
  showGroupTitles?: boolean;
  disableNameEllipsis?: boolean;
};

export const TeamPlayersListWithSearch = ({
  team,
  players,
  onPlayerClick,
  singleCurrentlyTrackedObjectId,
  showGroupTitles,
  disableNameEllipsis,
}: Props) => {
  const [search, setSearch] = useState('');
  const theme = useTheme();

  // Report search intent rather than keystrokes: without the debounce this
  // emits one event per character typed.
  useEffect(() => {
    const query = search.trim();
    if (!query) return undefined;
    const timer = setTimeout(() => {
      Tracking.getInstance().track('Search Players', {
        category: 'Clips',
        searchQuery: query,
        teamId: team.id,
      });
    }, SEARCH_TRACK_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, team.id]);

  const teamFullName = team.displayName || team.name || team.abbreviation || 'Team';

  const filteredPlayers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return players;

    // Split into whitespace-separated tokens so the user can type any
    // combination of first name / last name / jersey in any order
    // (e.g. "mcd 97", "97 connor", "connor mcdavid"). Each token must
    // appear somewhere in the haystack of name + jersey.
    const tokens = query.split(/\s+/).filter(Boolean);

    return players.filter((p) => {
      const haystack = [p.displayName, p.playerNumber]
        .filter((v): v is string => Boolean(v))
        .map((v) => v.toLowerCase())
        .join(' ');
      return tokens.every((t) => haystack.includes(t));
    });
  }, [players, search]);

  // Override the default TrackedItem background when rendered in this search list
  // so the rows visually distinguish themselves from other contexts.
  const trackedItemSx = useMemo(
    () => ({ backgroundColor: theme.palette.cx.bg.elevated }),
    [theme],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ pt: 1.5, px: 1.5, pb: 0, flexShrink: 0 }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search for a player"
          size="small"
          fullWidth
          sx={{
            width: '100%',
            fontFamily: 'Sohne',
            fontSize: '11px',
            lineHeight: '14px',
            mb: 1,
            '& .MuiOutlinedInput-root': {
              height: '36px',
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.05)',
              borderRadius: '4px',
              padding: '8px 16px',
              gap: '12px',
              '& fieldset': {
                borderColor: 'rgba(255,255,255,0.2)',
                borderWidth: '1px',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255,255,255,0.3)',
              },
              '& .MuiInputBase-input': {
                padding: 0,
              },
            },
            '& .MuiInputBase-input::placeholder': {
              color: 'rgba(255,255,255,0.5)',
              opacity: 1,
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} color="rgba(255,255,255,0.5)" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <Box sx={{ flex: 1, overflow: 'hidden', pl:1 }}>
        <Typography
          sx={{
            mt: 1,
            fontSize: '13px',
            fontWeight: 500,
            color: 'text.secondary',
            letterSpacing: '0.01em',
            mb: 1,
            ml:0.5
          }}
        >
          {teamFullName}
        </Typography>

        <TeamPlayersList
          team={team}
          players={filteredPlayers}
          onPlayerClick={onPlayerClick}
          singleCurrentlyTrackedObjectId={singleCurrentlyTrackedObjectId}
          showGroupTitles={showGroupTitles}
          disableNameEllipsis={disableNameEllipsis}
          trackedItemSx={trackedItemSx}
        />
      </Box>
    </Box>
  );
};

export default TeamPlayersListWithSearch;
